import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link as LinkIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/components/ui/dialog'

interface DocsContentProps {
  content: string
  className?: string
}

/**
 * Component to render markdown documentation content
 * with proper styling and interactive features
 */
export function DocsContent({ content, className }: DocsContentProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)

  // Scroll to anchor on initial load or hash change
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1)
      if (hash && contentRef.current) {
        const element = contentRef.current.querySelector(`#${CSS.escape(hash)}`)
        if (element) {
          // Small delay to ensure content is rendered
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 100)
        }
      }
    }

    scrollToHash()
    window.addEventListener('hashchange', scrollToHash)
    return () => window.removeEventListener('hashchange', scrollToHash)
  }, [content])

  // Generate a slug from heading text
  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  return (
    <>
      <div
        ref={contentRef}
        className={cn(
          'prose prose-slate max-w-none',
          // Headings
          'prose-headings:scroll-mt-20 prose-headings:font-semibold',
          'prose-h1:text-3xl prose-h1:mb-4',
          'prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-b prose-h2:pb-2',
          'prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3',
          'prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2',
          // Links
          'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
          // Code
          'prose-code:before:content-none prose-code:after:content-none',
          'prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono',
          // Pre/code blocks
          'prose-pre:bg-muted prose-pre:border prose-pre:rounded-lg',
          // Images
          'prose-img:rounded-lg prose-img:shadow-md prose-img:cursor-pointer',
          // Tables
          'prose-table:border prose-table:border-border',
          'prose-th:bg-muted prose-th:px-4 prose-th:py-2 prose-th:border prose-th:border-border',
          'prose-td:px-4 prose-td:py-2 prose-td:border prose-td:border-border',
          // Lists
          'prose-ul:my-4 prose-ol:my-4',
          'prose-li:my-1',
          // Blockquotes
          'prose-blockquote:border-l-primary prose-blockquote:bg-muted/50 prose-blockquote:px-4 prose-blockquote:py-2 prose-blockquote:rounded-r-lg',
          // Print styles
          'print:prose-img:shadow-none',
          className
        )}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Custom heading components with anchor links
            h1: ({ children, ...props }) => {
              const text = extractTextFromChildren(children)
              const id = generateSlug(text)
              return (
                <h1 id={id} {...props} className="group relative">
                  {children}
                  <AnchorLink id={id} />
                </h1>
              )
            },
            h2: ({ children, ...props }) => {
              const text = extractTextFromChildren(children)
              const id = generateSlug(text)
              return (
                <h2 id={id} {...props} className="group relative">
                  {children}
                  <AnchorLink id={id} />
                </h2>
              )
            },
            h3: ({ children, ...props }) => {
              const text = extractTextFromChildren(children)
              const id = generateSlug(text)
              return (
                <h3 id={id} {...props} className="group relative">
                  {children}
                  <AnchorLink id={id} />
                </h3>
              )
            },
            h4: ({ children, ...props }) => {
              const text = extractTextFromChildren(children)
              const id = generateSlug(text)
              return (
                <h4 id={id} {...props} className="group relative">
                  {children}
                  <AnchorLink id={id} />
                </h4>
              )
            },
            // Custom link component to handle internal vs external links
            a: ({ href, children, ...props }) => {
              const isExternal = href?.startsWith('http') || href?.startsWith('//')
              const isAnchor = href?.startsWith('#')

              if (isExternal) {
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                )
              }

              if (isAnchor) {
                return (
                  <a href={href} {...props}>
                    {children}
                  </a>
                )
              }

              // Internal docs link - use relative path
              return (
                <a
                  href={href?.startsWith('/') ? href : `/dashboard/docs/${href}`}
                  onClick={(e) => {
                    e.preventDefault()
                    const targetPath = href?.startsWith('/') ? href : `/dashboard/docs/${href}`
                    window.history.pushState({}, '', targetPath)
                    window.dispatchEvent(new PopStateEvent('popstate'))
                  }}
                  {...props}
                >
                  {children}
                </a>
              )
            },
            // Custom image component with lightbox
            img: ({ src, alt, ...props }) => (
              <img
                src={src}
                alt={alt}
                onClick={() => src && setLightboxImage(src)}
                className="cursor-pointer hover:opacity-90 transition-opacity"
                {...props}
              />
            ),
            // Custom code block with copy button
            pre: ({ children, ...props }) => (
              <div className="relative group">
                <pre {...props} className="overflow-x-auto">
                  {children}
                </pre>
              </div>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      {/* Image Lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-4xl p-2 bg-transparent border-none shadow-none">
          <DialogClose className="absolute right-4 top-4 z-50" />
          {lightboxImage && (
            <img
              src={lightboxImage}
              alt="Enlarged view"
              className="w-full h-auto rounded-lg shadow-2xl"
              onClick={() => setLightboxImage(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Anchor link component for heading deep links
 */
function AnchorLink({ id }: { id: string }) {
  const handleClick = () => {
    const url = new URL(window.location.href)
    url.hash = id
    window.history.pushState({}, '', url.toString())
    navigator.clipboard?.writeText(url.toString())
  }

  return (
    <button
      onClick={handleClick}
      className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
      aria-label={`Copy link to ${id}`}
    >
      <LinkIcon className="h-4 w-4" />
    </button>
  )
}

/**
 * Helper function to extract text content from React children
 */
function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children
  }
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('')
  }
  if (children && typeof children === 'object' && 'props' in children) {
    return extractTextFromChildren((children as React.ReactElement).props.children)
  }
  return ''
}
